// SWR-lite data hook: module-level cache keyed by path, background
// revalidation on mount, optional polling, and optimistic `mutate`.
// Pass a null/undefined key to skip fetching (conditional requests).

import { useCallback, useEffect, useRef, useState } from 'react';
import { apiFetch } from '../api/client';

const cache = new Map();

export default function useApi(key, { refreshInterval } = {}) {
  const [data, setData] = useState(() => (key ? cache.get(key) : undefined));
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(key) && !cache.has(key));
  const keyRef = useRef(key);
  keyRef.current = key;

  const load = useCallback(async (showSpinner) => {
    const currentKey = keyRef.current;
    if (!currentKey) return;
    if (showSpinner) setIsLoading(true);
    try {
      const result = await apiFetch(currentKey);
      cache.set(currentKey, result);
      if (keyRef.current === currentKey) {
        setData(result);
        setError(null);
      }
    } catch (err) {
      if (keyRef.current === currentKey) setError(err);
    } finally {
      if (keyRef.current === currentKey) setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!key) {
      setData(undefined);
      setError(null);
      setIsLoading(false);
      return undefined;
    }
    setData(cache.get(key));
    setError(null);
    setIsLoading(!cache.has(key));
    load(false);
    if (refreshInterval) {
      const timer = setInterval(() => load(false), refreshInterval);
      return () => clearInterval(timer);
    }
    return undefined;
  }, [key, refreshInterval, load]);

  const mutate = useCallback((updater) => {
    const currentKey = keyRef.current;
    if (!currentKey) return;
    const previous = cache.get(currentKey);
    const next = typeof updater === 'function' ? updater(previous) : updater;
    cache.set(currentKey, next);
    setData(next);
  }, []);

  const refetch = useCallback(() => load(true), [load]);
  // Background reload without flipping isLoading (no skeleton flash after a mutation).
  const revalidate = useCallback(() => load(false), [load]);

  return { data, error, isLoading, refetch, revalidate, mutate };
}

export function clearApiCache() {
  cache.clear();
}

// Drops every cached entry whose key starts with `prefix` so the next mount
// (or revalidate) fetches fresh data — e.g. invalidate('/api/engine/') after
// any prospect action.
export function invalidate(prefix) {
  for (const key of [...cache.keys()]) {
    if (key.startsWith(prefix)) cache.delete(key);
  }
}
